import CheckIcon from '@mui/icons-material/Check';
import { Grid } from '@mui/material';
import { RoutalPalette, VSpacer } from '@nx-smartmonkey/ui';
import { useEffect } from 'react';

import Other from '../../../assets/svg/onboarding/icons/Other';
import SectionCommercial from '../../../assets/svg/onboarding/icons/SectionCommercial';
import SectionLogistics from '../../../assets/svg/onboarding/icons/SectionLogistics';
import SectionRetail from '../../../assets/svg/onboarding/icons/SectionRetail';
import SectionServices from '../../../assets/svg/onboarding/icons/SectionServices';
import { useUpdateOrganizationOnboarding } from '../../../hooks/organization/useUpdateOrganizationOnboarding';
import Button from '../../../react-components/Button';
import InputText from '../../../react-components/InputText';
import Text from '../../../react-components/Text';
import eventsPanel from '../../../services/events';
import { AnimatedStep } from './AnimatedStep';

const sections = [`retail`, `logistics`, `services`, `commercial`, `other`] as const;

export type SectionTypes = (typeof sections)[number];

export const getImageBySection = (section: SectionTypes | undefined) => {
  switch (section) {
    //TODO añadir nuevas imagenes
    case `retail`:
    case `logistics`:
    case `services`:
    case `commercial`:
    default:
      return null;
  }
};

interface SectionProps {
  section: SectionTypes | undefined;
  setSection: (section: SectionTypes | undefined) => void;
  otherSection: string;
  setOtherSection: (other: string) => void;
  onNextStep: () => void;
}

export const Section = ({ section, setSection, otherSection, setOtherSection, onNextStep }: SectionProps) => {
  const { success, run: updateOrganization } = useUpdateOrganizationOnboarding();

  useEffect(() => {
    if (success) {
      setTimeout(() => {
        onNextStep();
        eventsPanel.onboardingPageView({ page: `section`, data: { section } });
      }, 500);
    }
  });

  const isSectionActive = (s: SectionTypes) => section === s;

  const isNextButtonEnabled = () => {
    if (section === undefined) {
      return false;
    }

    if (section === `other` && otherSection === ``) {
      return false;
    }

    return true;
  };

  const getIconBySection = (s: SectionTypes) => {
    switch (s) {
      case `retail`:
        return <SectionRetail color={isSectionActive(s) ? RoutalPalette.primary40 : RoutalPalette.text} />;
      case `logistics`:
        return <SectionLogistics color={isSectionActive(s) ? RoutalPalette.primary40 : RoutalPalette.text} />;
      case `services`:
        return <SectionServices color={isSectionActive(s) ? RoutalPalette.primary40 : RoutalPalette.text} />;
      case `commercial`:
        return <SectionCommercial color={isSectionActive(s) ? RoutalPalette.primary40 : RoutalPalette.text} />;
      case `other`:
        return <Other color={isSectionActive(s) ? RoutalPalette.primary40 : RoutalPalette.text} />;
      default:
        return null;
    }
  };

  return (
    <AnimatedStep id="sectionAnimatedContainer" isVisible={!success}>
      <Grid
        container
        direction="column"
        wrap="nowrap"
        style={{ padding: `20px`, maxWidth: `660px`, position: `relative` }}
      >
        <Grid item>
          <Text variant="h6" intlId="company.onboarding.section.info" />
        </Grid>

        <VSpacer medium />

        <Grid item container direction="row" wrap="nowrap">
          {sections.map((s, index) => {
            return (
              <Grid
                key={s}
                item
                container
                direction="column"
                wrap="nowrap"
                alignItems="center"
                justifyContent="center"
                style={{
                  position: `relative`,
                  cursor: `pointer`,
                  borderRadius: `10px`,
                  padding: `12px`,
                  height: `100px`,
                  width: `140px`,

                  border: isSectionActive(s)
                    ? `2px solid ${RoutalPalette.primary40}`
                    : `1px solid ${RoutalPalette.neutral10}`,
                  marginRight: index < sections.length - 1 ? `12px` : `0px`,
                }}
                onClick={() => {
                  if (isSectionActive(s)) {
                    setSection(undefined);
                    if (s === `other`) {
                      setOtherSection(``);
                    }
                  } else {
                    setSection(s);
                  }
                }}
              >
                <div
                  style={{
                    position: `absolute`,
                    top: 0,
                    right: 0,
                    width: `16px`,
                    height: `16px`,
                    borderBottomLeftRadius: `10px`,
                    borderTopRightRadius: `5px`,
                    backgroundColor: isSectionActive(s) ? RoutalPalette.primary40 : `transparent`,
                    borderLeft: isSectionActive(s)
                      ? `2px solid ${RoutalPalette.primary40}`
                      : `1px solid ${RoutalPalette.neutral10}`,
                    borderBottom: isSectionActive(s)
                      ? `2px solid ${RoutalPalette.primary40}`
                      : `1px solid ${RoutalPalette.neutral10}`,
                  }}
                >
                  {isSectionActive(s) && (
                    <CheckIcon style={{ marginTop: `-1px`, marginLeft: `1px`, fontSize: `14px`, color: `white` }} />
                  )}
                </div>

                <Grid item>{getIconBySection(s)}</Grid>

                <Grid item>
                  <Text
                    intlId={`company.onboarding.section.option.${s}`}
                    style={{
                      cursor: `pointer`,
                      color: isSectionActive(s) ? RoutalPalette.primary40 : RoutalPalette.text,
                    }}
                  />
                </Grid>
              </Grid>
            );
          })}
        </Grid>
        <VSpacer medium />
        {section && (
          <>
            {isSectionActive(`other`) ? (
              <Grid container direction="column">
                <Grid item>
                  <InputText
                    onClickStopPropagation
                    fieldValue={otherSection}
                    placeholderIntlId="company.onboarding.section.option.other.ph"
                    inputContainerStyles={{ width: `100%` }}
                    onChange={(text: any) => {
                      setOtherSection(text);
                    }}
                  />
                </Grid>
              </Grid>
            ) : null}
          </>
        )}

        <Grid
          item
          container
          direction="row"
          wrap="nowrap"
          alignItems="center"
          justifyContent="center"
          style={{ position: `absolute`, bottom: `-60px`, height: `62px` }}
        >
          {isNextButtonEnabled() && (
            <Button
              intlId="company.onboarding.next"
              style={{
                width: `200px`,
                height: `32px`,
              }}
              onClick={() => {
                updateOrganization({
                  section: section === `other` ? otherSection : section,
                });
              }}
            />
          )}
        </Grid>
      </Grid>
    </AnimatedStep>
  );
};
